import { BASE_URL } from "../../store/services/URL";
import { abortableRequest } from "../../helpers/abortableRequest";
import { readPagination } from "../../helpers/apiResponse";

const CARD_CACHE_TTL_MS = 60 * 1000;

// Cards, performance figures and budgets already fetched, so reopening a resource within a minute does not hit the server again.
const cardCache = new Map();
const performanceCache = new Map();
const budgetCache = new Map();

/** The query string of some parameters; a list is written as the same parameter once for each value. */
function toQueryString(params) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    const values = Array.isArray(value) ? value : [value];
    values.forEach((item) => {
      if (item !== undefined && item !== null && item !== "") query.append(key, item);
    });
  });
  const text = query.toString();
  return text ? `?${text}` : "";
}

/**
 * GET /resources/{id}/card
 * @param {string} resourceId
 * @param {{ projectId?: string, startDate?: string, endDate?: string }} params - project scope and utilization period
 * @param {AbortSignal} [signal]
 */
export function getResourceCard(resourceId, { projectId, startDate, endDate }, signal) {
  const cacheKey = [resourceId, projectId, startDate, endDate].join("|");
  const cached = cardCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < CARD_CACHE_TTL_MS) {
    return Promise.resolve(cached.card);
  }

  return abortableRequest({
    url: `${BASE_URL}resources/${resourceId}/card${toQueryString({ projectId, startDate, endDate })}`,
    signal,
  }).then((response) => {
    cardCache.set(cacheKey, { card: response.data, fetchedAt: Date.now() });
    return response.data;
  });
}

/**
 * GET /resources/{id}/assignments
 * @param {string} resourceId
 * @param {Object} params - the filters of `assignmentQuery` plus { pageNumber, pageSize, orderBy }
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ items: Array, pagination: Object|null }>}
 */
export function getResourceAssignments(resourceId, params, signal) {
  return abortableRequest({
    url: `${BASE_URL}resources/${resourceId}/assignments${toQueryString(params)}`,
    signal,
  }).then((response) => ({
    items: Array.isArray(response.data) ? response.data : [],
    pagination: readPagination(response),
  }));
}

/**
 * GET /resources/{id}/performance
 * @param {string} resourceId
 * @param {{ projectId?: string, startDate?: string, endDate?: string }} params - project scope and period
 * @param {AbortSignal} [signal]
 */
export function getResourcePerformance(resourceId, { projectId, startDate, endDate }, signal) {
  const cacheKey = [resourceId, projectId, startDate, endDate].join("|");
  const cached = performanceCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < CARD_CACHE_TTL_MS) {
    return Promise.resolve(cached.performance);
  }

  return abortableRequest({
    url: `${BASE_URL}resources/${resourceId}/performance${toQueryString({ projectId, startDate, endDate })}`,
    signal,
  }).then((response) => {
    performanceCache.set(cacheKey, { performance: response.data, fetchedAt: Date.now() });
    return response.data;
  });
}

/**
 * GET /resources/{id}/budget
 * The budget is cumulative over the whole cashflow line, so it has no period.
 * @param {string} resourceId
 * @param {{ projectId?: string }} params - project scope
 * @param {AbortSignal} [signal]
 */
export function getResourceBudget(resourceId, { projectId }, signal) {
  const cacheKey = [resourceId, projectId].join("|");
  const cached = budgetCache.get(cacheKey);
  if (cached && Date.now() - cached.fetchedAt < CARD_CACHE_TTL_MS) {
    return Promise.resolve(cached.budget);
  }

  return abortableRequest({
    url: `${BASE_URL}resources/${resourceId}/budget${toQueryString({ projectId })}`,
    signal,
  }).then((response) => {
    budgetCache.set(cacheKey, { budget: response.data, fetchedAt: Date.now() });
    return response.data;
  });
}

/** Forgets every cached card, set of performance figures and budget; called when allocations or tasks change so a card never shows stale figures. */
export function invalidateResourceInsights() {
  cardCache.clear();
  performanceCache.clear();
  budgetCache.clear();
}
